import type { BasicIntlShape } from './intl'

export type PlanKey = 'basic' | 'pro' | 'enterprise'

export type PlanHighlight = {
  label: string
  // Nested under the preceding highlight.
  indent?: boolean
  // Show the standard connector-icon row beneath the highlight.
  showSourceIcons?: boolean
  tooltip?: { label: string; linkLabel?: string; href?: string }
}

export type SlitePlan = {
  id: PlanKey
  title: string
  description: string
  highlights: PlanHighlight[]
}

export function getSlitePlan(intl: BasicIntlShape, id: PlanKey): SlitePlan {
  const plan = getSlitePlans(intl).find((p) => p.id === id)
  if (!plan) {
    throw new Error(`Unknown plan: ${id}`)
  }
  return plan
}

export const CREDITS_HELP_CENTER_URL =
  'https://slite.com/help/mbbEbTqWSmK3zx/Credits'

// Per-plan content (title, tagline, condensed highlights) shown on the
// website plan cards and the in-app trial/paywall modal, so the two never drift.
export function getSlitePlans(intl: BasicIntlShape): SlitePlan[] {
  return [
    {
      id: 'basic',
      title: intl.formatMessage({
        id: 'pricing.basic.title',
        defaultMessage: 'Basic',
      }),
      description: intl.formatMessage({
        id: 'pricing.basic.desc',
        defaultMessage:
          'Everything you need to write, organize, and verify docs.',
      }),
      highlights: [
        {
          label: intl.formatMessage({
            id: 'pricing.basic.feature.1',
            defaultMessage: 'Unlimited docs',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.basic.feature.2',
            defaultMessage: 'Ask (AI search & answers)',
          }),
          tooltip: {
            label: intl.formatMessage({
              id: 'pricing.basic.feature.2.tooltip',
              defaultMessage: '30 questions per seat/month',
            }),
          },
        },
        {
          label: intl.formatMessage({
            id: 'pricing.basic.feature.3',
            defaultMessage: 'Doc verification workflow',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.basic.feature.4',
            defaultMessage: 'Knowledge Management Panel',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.basic.feature.6',
            defaultMessage: 'MCP and API access',
          }),
        },
      ],
    },
    {
      id: 'pro',
      title: intl.formatMessage({
        id: 'pricing.core.title',
        defaultMessage: 'Pro',
      }),
      description: intl.formatMessage({
        id: 'pricing.core.desc',
        defaultMessage:
          'An agent-powered knowledge base that stays in sync with your tools.',
      }),
      highlights: [
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.1',
            defaultMessage: 'Everything in Basic, plus:',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.2',
            defaultMessage: 'The Slite Agent',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.3',
            defaultMessage: 'Search across your connected tools',
          }),
          indent: true,
          showSourceIcons: true,
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.4',
            defaultMessage: 'Doc fact-checking with suggested fixes',
          }),
          indent: true,
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.agentWorkflows',
            defaultMessage: 'Agent workflows',
          }),
          indent: true,
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.credits',
            defaultMessage: '50 monthly credits per seat',
          }),
          tooltip: {
            label: intl.formatMessage({
              id: 'pricing.core.feature.credits.tooltip.prefix',
              defaultMessage: 'Read more about',
            }),
            linkLabel: intl.formatMessage({
              id: 'pricing.core.feature.credits.tooltip.linkLabel',
              defaultMessage: 'how credits work',
            }),
            href: CREDITS_HELP_CENTER_URL,
          },
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.6',
            defaultMessage: 'OpenID SSO',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.core.feature.7',
            defaultMessage: 'Custom domain for public docs',
          }),
        },
      ],
    },
    {
      id: 'enterprise',
      title: intl.formatMessage({
        id: 'pricing.enterprise.title',
        defaultMessage: 'Enterprise',
      }),
      description: intl.formatMessage({
        id: 'pricing.enterprise.desc',
        defaultMessage:
          'Advanced security, controls, and support for larger teams.',
      }),
      highlights: [
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.1',
            defaultMessage: 'Everything in Pro, plus:',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.2',
            defaultMessage: 'Reader-only seats',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.3',
            defaultMessage: 'Advanced security and controls',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.4',
            defaultMessage: 'Audit logs',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.5',
            defaultMessage: 'Service Level Agreement',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.6',
            defaultMessage: 'Dedicated account manager',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.priority',
            defaultMessage: 'Priority support',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.7',
            defaultMessage: 'Custom agent sources',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.8',
            defaultMessage: 'Migration and onboarding support',
          }),
        },
        {
          label: intl.formatMessage({
            id: 'pricing.enterprise.feature.9',
            defaultMessage: 'SCIM',
          }),
        },
      ],
    },
  ]
}
